import { Grid2, Link, Typography } from '@mui/material'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import DatePicker from 'components/DatePicker'
import { settingsPath } from 'components/Routes'
import FormField from 'components/formik/FormField'
import DefaultLayout from 'components/layout/DefaultLayout'
import { Formik } from 'formik'
import { useUserProfile, useMutateUserProfile } from 'hooks/useUserProfile'
import { useCallback, useMemo, useState } from 'react'
import { Address, Country, WidgetInput } from 'react-addressfinder'
import { useNavigate } from 'react-router-dom'
import {
  API_DATE_FORMAT,
  UI_DATE_FORMAT,
  formatAddress,
  formatDate
} from 'utils/util'
import * as Yup from 'yup'
import { formFieldData } from './formFieldData'
import useStyles from './styles'
export interface FormValues {
  address: string
  addressLine1: string
  addressLine2: string
  city: string
  suburb: string
  postcode: string
  dateOfBirth: string
  email: string
  legalName: string
  nzMobilePhoneNumber: string
  preferredName: string
  enterManually: boolean
}

interface IAddress {
  addressLine1?: string | null
  addressLine2?: string | null
  suburb?: string | null
  city?: string | null
  postcode?: string | null
}

const validationSchema = Yup.object().shape({
  legalName: Yup.string().required('Legal name is required'),
  address: Yup.string().when('enterManually', {
    is: (enterManually: boolean) => !enterManually,
    then: Yup.string().required('Address is required')
  }),
  addressLine1: Yup.string().when('enterManually', {
    is: (enterManually: boolean) => enterManually,
    then: Yup.string().required('AddressLine1 is required')
  }),
  city: Yup.string().when('enterManually', {
    is: (enterManually: boolean) => enterManually,
    then: Yup.string().required('City is required')
  }),
  postcode: Yup.string().when('enterManually', {
    is: (enterManually: boolean) => enterManually,
    then: Yup.string().required('Postcode is required')
  }),
  nzMobilePhoneNumber: Yup.string().required('Mobile phone number is required')
})

export default function PersonalDetails(): JSX.Element {
  const { classes } = useStyles()

  const { setToast } = useAuth()

  const [enterManually, setEnterManually] = useState<boolean>(false)

  const navigate = useNavigate()

  const { data: profile, isLoading: loading } = useUserProfile()

  const { mutateAsync: updateProfile, isLoading: updateProfileLoading } =
    useMutateUserProfile()

  function generateAddress(address: IAddress) {
    const { addressLine1, addressLine2, suburb, city, postcode } = address
    return [addressLine1, addressLine2, suburb, city, postcode]
      .filter(a => !!a)
      .join(', ')
  }

  const initialValues: FormValues = useMemo(
    () => {
      let values: FormValues = {
        address: '',
        addressLine1: profile?.address_line1 || '',
        addressLine2: profile?.address_line2 || '',
        suburb: profile?.suburb || '',
        city: profile?.city || '',
        postcode: profile?.postcode || '',
        dateOfBirth: profile?.date_of_birth || '',
        email: profile?.email || '',
        legalName: '',
        nzMobilePhoneNumber: profile?.mobile_phone_number || '',
        preferredName: profile?.preferred_name || '',
        enterManually: false
      }

      if (profile) {
        const {
          address_line1,
          address_line2,
          city,
          date_of_birth,
          first_name,
          last_name,
          postcode,
          suburb
        } = profile

        const legalName = [first_name, last_name].filter(n => !!n).join(' ')

        const address = generateAddress({
          addressLine1: address_line1,
          addressLine2: address_line2,
          city,
          postcode,
          suburb
        })

        values = {
          ...values,
          address: address || '',
          dateOfBirth: date_of_birth
            ? formatDate({
                date: date_of_birth,
                inputFormat: API_DATE_FORMAT,
                outputFormat: UI_DATE_FORMAT
              })
            : '',
          legalName: legalName || ''
        } as FormValues
      }

      return values
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile]
  )

  function formattedAddressValue(values: FormValues): string {
    const address = {
      city: values.city,
      country: Country.NZ,
      line1: values.addressLine1,
      line2: values.addressLine2,
      postcode: values.postcode,
      suburb: values.suburb
    }

    return address.line1 && address.city && address.postcode
      ? formatAddress(address)
      : ''
  }

  function handleSelectAddress(
    _fullAddress: string,
    address: Address,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) {
    const { city, line1, line2, postcode, suburb } = address

    setFieldValue('addressLine1', line1)
    setFieldValue('addressLine2', line2)
    setFieldValue('suburb', suburb)
    setFieldValue('city', city)
    setFieldValue('postcode', postcode)
    setFieldValue(
      'address',
      [line1, line2, suburb, city, postcode].filter(a => !!a).join(', ')
    )
  }

  const changeChangeAddress = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    setFieldValue('addressLine1', '')
    setFieldValue('addressLine2', '')
    setFieldValue('suburb', '')
    setFieldValue('city', '')
    setFieldValue('postcode', '')
    setFieldValue('address', '')
  }

  const onToggleEnterManually = (
    setFieldValue: (
      field: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      shouldValidate?: boolean
    ) => void,
    values: FormValues
  ) => {
    setEnterManually(!enterManually)
    setFieldValue('enterManually', !enterManually)

    if (!enterManually) {
      const address = generateAddress(values)
      setFieldValue('address', address)
    }
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      await updateProfile({
        preferred_name: values.preferredName,
        mobile_phone_number: values.nzMobilePhoneNumber,
        address_line1: values.addressLine1,
        address_line2: values.addressLine2 ?? '',
        city: values.city,
        suburb: values.suburb ?? '',
        postcode: values.postcode
      })
      setToast({
        open: true,
        message: 'Update Personal Details Successfully',
        type: 'success',
        duration: 1500
      })
      setTimeout(() => {
        navigate(settingsPath())
      }, 1500)
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : 'Failed to update profile'
      setToast({ open: true, message: msg, type: 'error' })
    }
  }

  const renderHeader = useCallback(() => {
    return (
      <Typography component='h1' variant='h1' fontSize='4.5rem' color='primary'>
        Personal details
      </Typography>
    )
  }, [])

  return (
    <DefaultLayout
      heading={renderHeader()}
      wrapperContainer={{ mb: 4 }}
      showBackIcon
      loading={loading}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, handleSubmit, values, errors, touched }) => (
          <form onSubmit={handleSubmit}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12 }}>
                <FormField
                  {...formFieldData.legalName}
                  error={!!(errors.legalName && touched.legalName)}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <FormField {...formFieldData.preferredName} />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <FormField
                  {...formFieldData.email}
                  error={!!(errors.email && touched.email)}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <DatePicker<FormValues>
                  name='dateOfBirth'
                  label='Date of birth'
                  inputFormat={UI_DATE_FORMAT}
                  outputFormat={UI_DATE_FORMAT}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <FormField
                  {...formFieldData.nzMobilePhoneNumber}
                  error={
                    !!(
                      errors.nzMobilePhoneNumber && touched.nzMobilePhoneNumber
                    )
                  }
                />
              </Grid2>
              <Grid2 container spacing={2} size={{ xs: 12 }}>
                {enterManually ? (
                  <>
                    <Grid2 size={{ xs: 12 }}>
                      <Link
                        color='textPrimary'
                        component='button'
                        variant='body1'
                        onClick={() =>
                          onToggleEnterManually(setFieldValue, values)
                        }
                        type='button'
                      >
                        Use address autocompletion
                      </Link>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                      <FormField
                        {...formFieldData.addressLine1}
                        error={!!(errors.addressLine1 && touched.addressLine1)}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                      <FormField {...formFieldData.addressLine2} />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                      <FormField {...formFieldData.suburb} />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 8 }}>
                      <FormField
                        {...formFieldData.city}
                        error={!!(errors.city && touched.city)}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                      <FormField
                        {...formFieldData.postcode}
                        error={!!(errors.postcode && touched.postcode)}
                      />
                    </Grid2>
                  </>
                ) : (
                  <>
                    <Grid2 size={{ xs: 12 }}>
                      <WidgetInput
                        addressFinderKey={
                          process.env.REACT_APP_ADDRESSFINDER_KEY as string
                        }
                        country={Country.NZ}
                        id='addressFinderWidget'
                        inputClassName={
                          errors.address
                            ? classes.addressFinderInputError
                            : classes.addressFinderInput
                        }
                        name='addressFinderWidget'
                        onSelected={(fullAddress, address) =>
                          handleSelectAddress(
                            fullAddress,
                            address,
                            setFieldValue
                          )
                        }
                        onChange={() => changeChangeAddress(setFieldValue)}
                        placeholder='Start typing your address...'
                        defaultValue={formattedAddressValue(values)}
                      />
                      {errors.address && (
                        <Typography className={classes.error}>
                          {errors.address}
                        </Typography>
                      )}
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                      <Link
                        color='textPrimary'
                        component='button'
                        onClick={() =>
                          onToggleEnterManually(setFieldValue, values)
                        }
                        type='button'
                      >
                        Not working? Enter manually
                      </Link>
                    </Grid2>
                  </>
                )}
              </Grid2>
              <Grid2 sx={{ mt: 4, textAlign: 'center' }} size={{ xs: 12 }}>
                <Button
                  color='primary'
                  type='submit'
                  fullWidth
                  disabled={updateProfileLoading}
                >
                  Save
                </Button>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Typography variant='body1'>
                  If you need to change any other details, please get in touch
                  with us at{' '}
                  <Link
                    color='textPrimary'
                    href='mailto:support@getgoldie.co.nz'
                  >
                    support@getgoldie.co.nz
                  </Link>
                  .
                </Typography>
              </Grid2>
            </Grid2>
          </form>
        )}
      </Formik>
    </DefaultLayout>
  )
}
